<template>
  <div class="source-documents" :class="`source-documents--${variant}`">
    <ul v-if="documents.length" class="source-documents__list">
      <li
        v-for="document in documents"
        :key="document.id"
        class="source-document"
        :class="{ 'source-document--failed': document.status === 'failed' }"
      >
        <template v-if="confirming === document.id">
          <span class="source-document__question">
            {{ t('popup.content.sources.confirmRemove', { name: documentLabel(document) }) }}
          </span>
          <button
            :ref="setConfirmButton"
            type="button"
            class="source-document__action source-document__action--danger"
            @click="confirmRemove(document.id)"
          >
            {{ t('popup.content.sources.delete') }}
          </button>
          <button type="button" class="source-document__action" @click="confirming = null">
            {{ t('popup.content.sources.cancel') }}
          </button>
        </template>
        <template v-else>
          <i class="fas" :class="documentIcon(document)" aria-hidden="true"></i>
          <span class="source-document__name" :title="documentTitle(document)">
            {{ documentLabel(document) }}
          </span>
          <span v-if="documentState(document)" class="sr-only">
            {{ documentState(document) }}
          </span>
          <button
            type="button"
            class="source-document__remove"
            :aria-label="t('popup.content.sources.remove', { name: documentLabel(document) })"
            :title="t('popup.content.sources.remove', { name: documentLabel(document) })"
            @click="askRemove(document.id)"
          >
            <i class="fas fa-trash" aria-hidden="true"></i>
          </button>
        </template>
      </li>
    </ul>
    <p v-else class="source-documents__empty">
      {{ emptyText ?? t('popup.content.sources.none') }}
    </p>

    <form v-if="addingLink" class="source-documents__link" @submit.prevent="submitLink">
      <input
        ref="linkInput"
        v-model="link"
        type="url"
        required
        class="source-documents__link-input"
        :aria-label="t('popup.content.sources.linkLabel')"
        :placeholder="t('popup.content.sources.linkPlaceholder')"
        @keydown.esc.stop="closeLink"
      />
      <button type="submit" :class="buttonClass" :disabled="uploading">
        {{ t('popup.content.sources.addLink') }}
      </button>
      <button type="button" :class="buttonClass" @click="closeLink">
        {{ t('popup.content.sources.cancel') }}
      </button>
    </form>
    <div v-else class="source-documents__actions">
      <button type="button" :class="buttonClass" :disabled="uploading" @click="fileInput?.click()">
        <i class="fas" :class="uploading ? 'fa-spinner fa-spin' : 'fa-paperclip'" aria-hidden="true"></i>
        <span>{{ t('popup.content.sources.attach') }}</span>
      </button>
      <button type="button" :class="buttonClass" :disabled="uploading" @click="openLink">
        <i class="fas fa-link" aria-hidden="true"></i>
        <span>{{ t('popup.content.sources.attachLink') }}</span>
      </button>
    </div>
    <input
      ref="fileInput"
      type="file"
      hidden
      :accept="ACCEPTED_SOURCE_FILE_TYPES"
      @change="onFileSelected"
    />

    <p v-if="error !== null" class="source-documents__error" role="alert">
      {{ error || t('popup.content.sources.actionFailed') }}
    </p>
  </div>
</template>

<script setup lang="ts">
// A point's source documents with the means to add a file or a link and to
// delete one. Presentational: the view that shows it owns the list
// (`usePointSourceDocuments`, or KAIA's session for the map's own documents)
// and carries out what this emits. `form` sits in the content form's popup;
// `bar` is compact, for the Quick Edit Bar's popover and the KAIA panel.

import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ACCEPTED_SOURCE_FILE_TYPES, isReading } from '../sourceDocumentFiles';
import type { SourceDocument } from '../types/SourceDocumentTypes';

const props = withDefaults(
  defineProps<{
    documents: SourceDocument[];
    uploading: boolean;
    /** The backend's reason a call failed, '' for none given, null when none did. */
    error: string | null;
    variant?: 'form' | 'bar';
    /** What an empty list says; by default, that the point has none. */
    emptyText?: string;
  }>(),
  { variant: 'form' }
);
const emit = defineEmits<{
  (event: 'upload', file: File): void;
  (event: 'attach-url', url: string): void;
  (event: 'remove', documentId: number): void;
}>();

const { t } = useI18n();

const buttonClass = computed(() =>
  props.variant === 'bar'
    ? 'button button--plain button--h-25 bg--white color--black font-weight--500'
    : 'button button--popup'
);

const fileInput = ref<HTMLInputElement | null>(null);

function onFileSelected(event: Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) emit('upload', file);
  input.value = ''; // allow re-picking the same file
}

const addingLink = ref(false);
const link = ref('');
const linkInput = ref<HTMLInputElement | null>(null);

function openLink(): void {
  addingLink.value = true;
  void nextTick(() => linkInput.value?.focus());
}

function closeLink(): void {
  addingLink.value = false;
  link.value = '';
}

function submitLink(): void {
  const url = link.value.trim();
  if (url) emit('attach-url', url);
  closeLink();
}

// Deleting asks first: the text is gone with it, and reading it again costs
// the pages a second time.
const confirming = ref<number | null>(null);
let confirmButton: HTMLButtonElement | null = null;

function setConfirmButton(element: unknown): void {
  confirmButton = element as HTMLButtonElement | null;
}

function askRemove(documentId: number): void {
  confirming.value = documentId;
  void nextTick(() => confirmButton?.focus());
}

function confirmRemove(documentId: number): void {
  confirming.value = null;
  emit('remove', documentId);
}

function documentLabel(document: SourceDocument): string {
  return document.url ?? document.filename ?? t('popup.content.sources.untitled');
}

function documentIcon(document: SourceDocument): string {
  if (isReading(document.status)) return 'fa-spinner fa-spin';
  return document.kind === 'url' ? 'fa-link' : 'fa-file';
}

// What the name alone does not say: still being read, why it failed, or that
// it came from a point that no longer exists (only the map's list has those).
function documentState(document: SourceDocument): string {
  if (isReading(document.status)) return t('popup.content.sources.reading');
  if (document.status === 'failed') {
    return document.error ?? t('popup.content.sources.failed');
  }
  if (document.point_deleted) return t('popup.content.sources.fromDeletedPoint');
  return '';
}

function documentTitle(document: SourceDocument): string {
  const state = documentState(document);
  return state ? `${documentLabel(document)} — ${state}` : documentLabel(document);
}
</script>

<style scoped lang="scss">
@use "@/scss/local" as *;

.source-documents__empty {
  margin-bottom: 15px;
  opacity: 0.8;
}

.source-documents__list {
  margin: 0 0 15px;
  padding: 0;
  list-style: none;
}

.source-document {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.source-document__name,
.source-document__question {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-document--failed .source-document__name {
  text-decoration: line-through;
}

.source-document__remove,
.source-document__action {
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}

.source-document__action {
  padding: 2px 6px;
  text-decoration: underline;

  &--danger {
    color: $error;
    font-weight: 600;
  }
}

.source-documents__actions,
.source-documents__link {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.source-documents__link-input {
  flex: 1;
  min-width: 180px;
  padding: 4px 8px;
}

.source-documents__error {
  margin-top: 15px;
  color: $error;
}

.source-documents--bar {
  font-size: 12px;

  .source-documents__empty,
  .source-documents__list {
    margin-bottom: 10px;
  }

  .source-documents__error {
    margin-top: 10px;
  }
}
</style>
